import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Empty, Input, Select, Skeleton, Tag, message } from 'antd';
import { SendOutlined, ThunderboltOutlined } from '@ant-design/icons';

import { authApi } from '../lib/authApi';
import { questionsApi, wikiApi, type WikiQuestionDto } from '../lib/wikiApi';
import { kbHintQuery } from '../lib/api/kb';
import { Button, Tag as DsTag, type TagColor } from '../components/ds';
import './KnowledgeBase.css';

const STATUS_META: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'Новый', color: 'orange' },
  ANSWERED: { label: 'Отвечен', color: 'green' },
  ARCHIVED: { label: 'В архиве', color: 'default' },
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Задать вопрос УК (HUB 2.0, прототип «ask») ─────────────────────

const ASK_TAG: Record<string, TagColor> = { OPEN: 'orange', ANSWERED: 'green', ARCHIVED: 'default' };

/**
 * «Задать вопрос УК»: тема (раздел), вопрос и подсказка «Похоже, ответ уже
 * есть» — пока пишете, ищем статьи по значимым словам (searchSuggest с ACL);
 * «Сначала спросить AI» → /ai. Справа — «Мои вопросы» со статусом и ответом.
 * ?q= из поиска/AI подставляется в текст.
 */
export function AskQuestionPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [question, setQuestion] = useState(params.get('q') ?? '');
  const [blockId, setBlockId] = useState<string | undefined>();

  const blocksQuery = useQuery({ queryKey: ['wiki', 'blocks'], queryFn: () => wikiApi.listBlocks(), staleTime: 300_000 });
  const mineQuery = useQuery({ queryKey: ['wiki', 'questions', 'mine'], queryFn: () => questionsApi.mine(), retry: false });

  // Подсказка из базы: запрос по значимым словам, с задержкой 500 мс.
  const [hintQ, setHintQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setHintQ(kbHintQuery(question)), 500);
    return () => clearTimeout(t);
  }, [question]);
  const hintQuery = useQuery({
    queryKey: ['wiki', 'search', 'suggest', 'ask-hint', hintQ],
    queryFn: () => wikiApi.searchSuggest(hintQ),
    enabled: hintQ.length >= 3,
    staleTime: 60_000,
  });
  const hints = (hintQuery.data?.items ?? []).slice(0, 2);

  const askMutation = useMutation({
    mutationFn: () => questionsApi.ask(question, blockId ?? null),
    onSuccess: async () => {
      message.success('Вопрос отправлен — УК ответит и пополнит базу знаний');
      setQuestion('');
      setBlockId(undefined);
      await qc.invalidateQueries({ queryKey: ['wiki', 'questions', 'mine'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const canSend = question.trim().length >= 5 && !askMutation.isPending;

  return (
    <div className="fk-kb-ask-grid">
      <section className="fk-kb-panel" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: -0.4 }}>Задать вопрос УК</h1>
        <div style={{ fontSize: 13, color: 'var(--fk-text-2)' }}>
          Ответ придёт в HUB, обычно в течение рабочего дня. Если вопрос частый — ответ станет статьёй базы знаний.
        </div>
        <div>
          <label className="fk-kb-label">Тема</label>
          <Select
            allowClear
            value={blockId}
            onChange={setBlockId}
            placeholder="К какому разделу относится вопрос?"
            style={{ width: '100%' }}
            options={(blocksQuery.data ?? []).map((b) => ({ value: b.id, label: b.name }))}
          />
        </div>
        <div>
          <label className="fk-kb-label" htmlFor="fk-kb-ask-text">
            Вопрос
          </label>
          <textarea
            id="fk-kb-ask-text"
            className="fk-kb-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Опишите ситуацию — чем конкретнее, тем быстрее ответ"
            maxLength={2000}
          />
        </div>
        {hints.length > 0 && (
          <div className="fk-kb-tip">
            <ThunderboltOutlined />
            <span>
              Похоже, ответ уже есть:{' '}
              {hints.map((h, i) => (
                <span key={h.id}>
                  {i > 0 && ', '}
                  <Link to={`/article/${h.slug}`} style={{ fontWeight: 600 }}>
                    «{h.title}»
                  </Link>
                </span>
              ))}
            </span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="primary" icon={<SendOutlined />} disabled={!canSend} loading={askMutation.isPending} onClick={() => askMutation.mutate()}>
            Отправить
          </Button>
          <Button
            variant="ghost"
            icon={<ThunderboltOutlined />}
            onClick={() => navigate(question.trim() ? `/ai?q=${encodeURIComponent(question.trim())}` : '/ai')}
          >
            Сначала спросить AI
          </Button>
        </div>
      </section>

      <section className="fk-kb-panel fk-kb-panel-pad">
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Мои вопросы</div>
        {mineQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        ) : (mineQuery.data?.length ?? 0) === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--fk-text-muted)', padding: '6px 0' }}>Вы ещё не задавали вопросов</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {mineQuery.data!.map((q) => {
              const meta = STATUS_META[q.status] ?? STATUS_META.OPEN;
              return (
                <div key={q.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--fk-row-line)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere' }}>{q.question}</div>
                    <DsTag color={ASK_TAG[q.status] ?? 'default'}>{meta.label}</DsTag>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 4 }}>{fmtDate(q.createdAt)}</div>
                  {q.answer && (
                    <div className="fk-kb-answer">
                      {q.answeredByName && <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)', marginBottom: 2 }}>{q.answeredByName}</div>}
                      {q.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Инбокс «Вопросы партнёров» (УК) ───────────────────────────────

export function QuestionInboxPage(): JSX.Element {
  const qc = useQueryClient();
  const meQuery = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const canAdmin = meQuery.data?.canAdmin ?? false;
  const [filter, setFilter] = useState<'OPEN' | 'ANSWERED' | 'ALL'>('OPEN');

  const inboxQuery = useQuery({
    queryKey: ['wiki', 'questions', 'inbox', filter],
    queryFn: () => questionsApi.inbox(filter),
    enabled: canAdmin,
    retry: false,
  });

  const answerMutation = useMutation({
    mutationFn: ({ id, answer }: { id: string; answer: string }) => questionsApi.answer(id, answer),
    onSuccess: async () => {
      message.success('Ответ сохранён');
      await qc.invalidateQueries({ queryKey: ['wiki', 'questions', 'inbox'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const archiveMutation = useMutation({
    mutationFn: (id: string) => questionsApi.archive(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki', 'questions', 'inbox'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (meQuery.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!canAdmin) {
    return <Alert type="warning" showIcon message="Недостаточно прав" description="Инбокс вопросов доступен администраторам базы знаний (kb:admin)." />;
  }

  const rows = inboxQuery.data ?? [];

  return (
    <div style={{ maxWidth: 900 }}>
      <h1 className="fk-page-title">Вопросы партнёров</h1>
      <p className="fk-page-sub">Отвечайте на вопросы — так база знаний пополняется живыми пробелами.</p>

      <div style={{ display: 'flex', gap: 8, margin: '16px 0' }}>
        {([['OPEN', 'Новые'], ['ANSWERED', 'Отвеченные'], ['ALL', 'Все']] as const).map(([val, label]) => (
          <button key={val} type="button" className={`fk-chip ${filter === val ? 'fk-chip-active' : ''}`} onClick={() => setFilter(val)}>
            {label}
          </button>
        ))}
      </div>

      {inboxQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : rows.length === 0 ? (
        <Empty description="Вопросов нет" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((q) => (
            <InboxItem
              key={q.id}
              q={q}
              onAnswer={(answer) => answerMutation.mutate({ id: q.id, answer })}
              onArchive={() => archiveMutation.mutate(q.id)}
              busy={answerMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InboxItem({ q, onAnswer, onArchive, busy }: { q: WikiQuestionDto; onAnswer: (a: string) => void; onArchive: () => void; busy: boolean }): JSX.Element {
  const [answer, setAnswer] = useState(q.answer ?? '');
  const meta = STATUS_META[q.status] ?? STATUS_META.OPEN;
  return (
    <div className="fk-card fk-card-pad">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{q.question}</div>
        <Tag color={meta.color} style={{ margin: 0, height: 'fit-content' }}>{meta.label}</Tag>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
        {q.askedByName ?? 'Аноним'} · {fmtDate(q.createdAt)}
      </div>
      <div style={{ marginTop: 10 }}>
        <Input.TextArea value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Ответ партнёру…" autoSize={{ minRows: 2, maxRows: 8 }} />
        <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="fk-btn fk-btn-ghost" onClick={onArchive}>В архив</button>
          <button
            type="button"
            className="fk-btn fk-btn-primary"
            disabled={answer.trim().length < 2 || busy}
            style={{ opacity: answer.trim().length < 2 ? 0.6 : 1 }}
            onClick={() => onAnswer(answer)}
          >
            {q.status === 'ANSWERED' ? 'Обновить ответ' : 'Ответить'}
          </button>
        </div>
      </div>
    </div>
  );
}
